import { Injectable } from '@angular/core';
import { StackedSidebarComponent } from '../components/layout/stacked-sidebar/stacked-sidebar.component';
import { ModalComponent } from '../components/modal/modal.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BaseService } from './base.service';
import { ModalData } from '../model/modal-data.model';
import { Observable, of } from 'rxjs';
import { SidebarData, SidebarType } from '../components/layout/stacked-sidebar/stacked-sidebar.interface';
import { ChatsSidebarPayload } from '../components/layout/chat-sidebar/chat-sidebar.interface';
import { BinotelService } from './binotel.service';
import { ChatService } from './chat.service';


@Injectable({
  providedIn: 'root'
})
export class StackedSidebarService {

  constructor(
    private readonly baseService: BaseService,
    private readonly dialog: MatDialog,
    private readonly chatService: ChatService,
  ) {

  }

  public chatsOpened: boolean = false;
  private chatDialogRef?: MatDialogRef<ModalComponent>;

  public openSidebar<TResult = any>(config: any, payload: any): Observable<TResult | undefined> {
    const maxWidth = this.baseService.isMobile ? '100vw' : config.maxWidth ?? '90vw';
    const modalData: ModalData<any> = {
      component: StackedSidebarComponent,
      width: config.width ?? '50vw',
      maxWidth: maxWidth,
      hideHeader: config.hideHeader || true,
      data: {
        type: config.type,
        payload: payload,
      },
    };


    const modalRef = this.dialog.open<ModalComponent, ModalData<any>, TResult>(ModalComponent, {
        data: modalData,
        panelClass: ['stacked-sidebar-panel', ...(modalData.containerClass ? [modalData.containerClass].flat() : [])],
        backdropClass: modalData.backdropClass,
        enterAnimationDuration: modalData?.enterAnimationDuration || '0ms',
        exitAnimationDuration: modalData?.exitAnimationDuration || '0ms',
        autoFocus: modalData?.autoFocus ?? true,
        restoreFocus: modalData?.restoreFocus ?? false,
        width: this.baseService.isMobile ? '90vw' : modalData?.width,
        maxWidth: maxWidth,
        height: modalData?.height,
        disableClose: true,
      });

    return modalRef.afterClosed();
  }

  /** Whether a sidebar of the type is open, e.g. a card detail, over which a second one must not open */
  public isOpen(type: SidebarType): boolean {
    return this.dialog.openDialogs.some((ref) => {
      const data = (ref.componentInstance as { data?: ModalData<SidebarData> } | null)?.data;
      return data?.component === StackedSidebarComponent && data.data?.type === type;
    });
  }

  public openChats(chatId?: string, payload: ChatsSidebarPayload = {}): Observable<any> {
    if (this.chatsOpened) {
      this.chatService.selectChat(chatId ?? this.chatService.chatIdQuery);
      return of(undefined);
    }

    this.chatsOpened = true;
    this.chatService.initChatIdQuery(chatId);

    const modalData: ModalData<any> = {
      component: StackedSidebarComponent,
      hideHeader: true,
      fullWidth: true,
      data: {
        type: SidebarType.Chats,
        payload: { chatId, ...payload },
      },
    };

    this.chatDialogRef = this.dialog.open(ModalComponent, {
      data: modalData,
      autoFocus: false,
      restoreFocus: false,
      width: this.baseService.isMobile ? '100vw' : modalData.width,
      maxWidth: this.baseService.isMobile ? '100vw' : '80vw',
      panelClass: 'stacked-sidebar-panel',
      disableClose: true,
    });

    const closed$ = this.chatDialogRef.afterClosed();
    closed$.subscribe(() => {
      this.chatsOpened = false;
      this.chatDialogRef = undefined;
      this.chatService.resetChatIdQuery();
    });

    return closed$;
  }

  public closeChats(): void {
    this.chatDialogRef?.close();
  }
}
